import React, { useContext, useEffect, useState} from 'react';
import GrayButtonTabs from "../../../../ui/GrayButtonTabs";
import PrimaryButtonTabs from "../../../../ui/PrimaryButtonTabs";
import LeftRightSplit from '../../../../ui/LeftRightSplit';
import SecondaryButton from '../../../../ui/SecondaryButton';
import InlineInputControlRow from '../../../../ui/layout/InlineInputControlRow';
import TextBox from '../../../../ui/TextBox';
import { ApplicableTableSettings, TableSettings } from '../../../../tableSettings';
import { FloatRenderer, TextAlignLeftRenderer } from '../../../../uiControls/SmartTableRenderers';
import SmartTable from '../../../../uiControls/SmartTable';
import InlineInputControlRowNoLabel from '../../../../ui/layout/InlineInputControlRowNoLabel';
import Float from '../../../../ui/Float';
import Label from '../../../../ui/Label';
import Number from '../../../../ui/Number';
import { AmsHbl } from '../../../../fms.core/ams/AmsHbl';
import { useTypedFastStorage } from '../../../../hooks/useFastStorage';
import ApplicationContext from '../../../../ApplicationContext';
import _ from 'lodash';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import DeepBlueButtonTabs from '../../../../ui/DeepBlueButtonTabs';

const descriptionOfContainerSettings:TableSettings = {
  tableVersion: 1005,
  overflowX: 'hidden',
  overflowY: 'scroll',
  
  flexResizer: true,

  alternateRows: true,
  columns: [
    { accessor:'no', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.41, },
    { accessor:'containerNo', Header: 'Container #', visible: false, resizable: true, sortable: false, initialWidthPercentage: 13.24, },
    { accessor:'htsNo', Header: 'HTS #', visible: true, resizable: false, sortable: false, initialWidthPercentage: 11.76, },
    { accessor:'description', Header: 'Description', visible: true, resizable: false, sortable: false, initialWidthPercentage: 16.18, },
    { accessor:'marks', Header: 'Marks', visible: true, resizable: false, sortable: false, initialWidthPercentage: 16.18, },
    { accessor:'pkgs', Header: 'Pkgs', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.82, },
    { accessor:'pUnit', Header: 'Unit', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.82, },
    { accessor:'weight', Header: 'KGS', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.82, },
    { accessor:'volume', Header: 'CBM', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.82, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 2.95, },
  ]
};

const hazardousInfoSettings:TableSettings = {
  tableVersion: 1005,
  overflowX: 'hidden',
  overflowY: 'scroll',
  flexResizer: true,

  alternateRows: true,
  columns: [
    { accessor:'no', Header: '#', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.41, },
    { accessor:'containerNo', Header: 'Container #', visible: true, resizable: false, sortable: false, initialWidthPercentage: 14.71, },
    { accessor:'hmCode', Header: 'Code', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.82, },
    { accessor:'hmClass', Header: 'Class', visible: true, resizable: false, sortable: false, initialWidthPercentage: 5.88, },
    { accessor:'hmQualifier', Header: 'Qualifier', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.82, },
    { accessor:'hmDescript', Header: 'Description', visible: true, resizable: false, sortable: false, initialWidthPercentage: 14.71, },
    { accessor:'hmContact', Header: 'Contact', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.82, },
    { accessor:'hmUnpage', Header: 'Unpage', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.82, },
    { accessor:'flashPoint', Header: 'Flash Point', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.82, },
    { accessor:'flashUnit', Header: 'Unit', visible: true, resizable: false, sortable: false, initialWidthPercentage: 8.82, },
    { accessor:'negative', Header: 'Nag', visible: true, resizable: false, sortable: false, initialWidthPercentage: 4.41, },
    { accessor:'scroll', Header: '', visible: true, resizable: false, sortable: false, initialWidthPercentage: 2.96, },
  ]
};



function NoOfContainerRenderer(num: number) {
  return (
    <div style={{marginTop: '5px', maxWidth: '120px'}}>
      <LeftRightSplit
        leftSize="80px"
        rightSize="calc(100% - 80px)"
        left={<Label label="Container(s)" />}
        right={<Number value={num} disabled />}
      />
    </div>
  );
}

function TotaPkgsRenderer(num: number) {
  return (
    <div style={{display: 'flex', marginTop: '5px'}}>
      <div style={{margin: 'auto'}}></div>

      <div style={{width: '80px'}}>
        <LeftRightSplit
          leftSize="30px"
          rightSize="calc(100% - 30px)"
          left={<Label label="Total" />}
          right={<Float value={num} disabled />}
        />
      </div>
    </div>
  );
}

function TotalKgsRenderer(num: number) {
  return (
    <div style={{display: 'flex', marginTop: '5px'}}>
      <div style={{margin: 'auto'}}></div>

      <div style={{width: '80px'}}>
        <Float value={num} disabled />
      </div>
    </div>
  );
}

function TotalCbmRenderer(num: number) {
  return (
    <div style={{display: 'flex', marginTop: '5px'}}>
      <div style={{margin: 'auto'}}></div>

      <div style={{width: '80px'}}>
        <Float value={num} disabled />
      </div>
    </div>
  );
}

interface ViewHblStorageKeys {
  modelStorage: string,
};

interface ViewHblParams {
  id: string | undefined,
  onPopupClose?: () => void,
};

interface ViewHblLoadedDetailsViewParams {
  hbl: AmsHbl,
  onPopupClose?: () => void,
  useDarkMode: boolean
};

function getViewHblStorageKeys(): ViewHblStorageKeys {
  return {modelStorage: 'oi-ams-hbl'}
}

function ViewHblImpl(params: ViewHblParams) {
  const {id, onPopupClose} = params;
  const {modelStorage} = getViewHblStorageKeys();
  const [inStorage, setInStorage, removeHblFromStorage] = useTypedFastStorage<AmsHbl>(modelStorage, null);
  const appContext = useContext(ApplicationContext);

  useFunctionKeys({onEsc: cancel});

  function cancel() {
    if(onPopupClose) onPopupClose();
    return true;
  }

  useEffect(() => {
    initialize();
    return () => {
      removeHblFromStorage()
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []
);

async function loadExisting() {
  const apiResult = await appContext.api.getAmsHbl(id);
  if(apiResult && apiResult.success) {
    let data: AmsHbl = new AmsHbl(apiResult.result);
    return data;
  }
  return {}
}

async function initialize() {
  const data = await loadExisting();
  setInStorage(_.cloneDeep(data));
}

if (!inStorage) {
  return null;
}

  return (
    <ViewHblImplInitializedImpl 
      hbl={inStorage}
      onPopupClose={params.onPopupClose}
      useDarkMode={appContext.useDarkMode}
    />
  );
}

function IndicatorRenderer({row}: any) {
  return <div style={{textAlign: 'center', width: '100%'}}>{row.index + 1}</div>;
}

function renderTitleButton(label:React.ReactNode|string|null|undefined, selected:boolean, onClick: VoidFunction, useDarkMode: boolean) {
  const Button = !selected ? GrayButtonTabs : !useDarkMode ? PrimaryButtonTabs : DeepBlueButtonTabs;

  return <Button size='regular' style={{width: '100%'}} onClick={onClick}>{label}</Button>
}

function ViewHblImplInitializedImpl({
  hbl,
  onPopupClose,
  useDarkMode
}: ViewHblLoadedDetailsViewParams) {
  const [tab, setTab] = useState(0);

  function BlInfo() {
    return (
      <div style={{display: 'flex'}}>
        <div style={{width: '320px'}}>
          <InlineInputControlRow mode="smaller-2x" smallMargin label="HBL #">
            <LeftRightSplit
              leftSize="70px"
              rightSize="calc(100% - 70px)"
              left={<TextBox value={hbl.amsID} disabled />}
              right={<TextBox value={hbl.hblNo} disabled />}
            />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label="MBL #">
            <TextBox value={hbl.mblNo} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label="Place of Receipt">
            <TextBox value={hbl.placeOfRec} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label="Shipper">
            <TextBox value={hbl.sName} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <TextBox value={hbl.sAddress1} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <TextBox value={hbl.sAddress2} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <TextBox value={hbl.sAddress3} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <div style={{width: '70px'}}>
              <TextBox value={hbl.sPhone} disabled />
            </div>
          </InlineInputControlRow>
  
          <InlineInputControlRowNoLabel smallMargin>&nbsp;</InlineInputControlRowNoLabel>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label="Move Type">
            <div style={{width: '70px'}}>
              <TextBox value={hbl.moveType} disabled />
            </div>
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label="Foreign Destination">
            <LeftRightSplit
              leftSize="70px"
              rightSize="calc(100% - 70px)"
              left={<TextBox value={hbl.foreignDest} disabled />}
              right={<TextBox value={hbl.fPortName} disabled />}
            />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label="Final Destination">
            <LeftRightSplit
              leftSize="70px"
              rightSize="calc(100% - 70px)"
              left={<TextBox value={hbl.dCode1} disabled />}
              right={<TextBox value={hbl.dPortName1} disabled />}
            />
          </InlineInputControlRow>
        </div>
  
        <div style={{width: '320px', marginLeft: '40px'}}>
          <InlineInputControlRow mode="smaller-2x" smallMargin label="Consignee">
            <TextBox value={hbl.cName} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <TextBox value={hbl.cAddress1} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <TextBox value={hbl.cAddress2} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <TextBox value={hbl.cAddress3} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <div style={{width: '70px'}}>
              <TextBox value={hbl.cPhone} disabled />
            </div>
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label="Notify">
            <TextBox value={hbl.nName} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <TextBox value={hbl.nAddress1} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <TextBox value={hbl.nAddress2} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <TextBox value={hbl.nAddress3} disabled />
          </InlineInputControlRow>
  
          <InlineInputControlRow mode="smaller-2x" smallMargin label={<span>&nbsp;</span>}>
            <div style={{width: '70px'}}>
              <TextBox value={hbl.nPhone} disabled />
            </div>
          </InlineInputControlRow>
        </div>
  
      </div>
    );
  }

  function DescriptionOfContainer() {
    const tableSettings:ApplicableTableSettings = {
      customTableId: 'oi-mbl-cnt-desc',
      tableName: 'oi-mbl-cnt-desc',
      ...descriptionOfContainerSettings,
      height: '234px',
      data: hbl.hts.map(x => ({...x})),
      rowRenderers: [
        { accessor: 'no', Cell: IndicatorRenderer },
        { accessor: 'containerNo', Cell: TextAlignLeftRenderer },
        { accessor: 'htsNo', Cell: TextAlignLeftRenderer },
        { accessor: 'description', Cell: TextAlignLeftRenderer },
        { accessor: 'marks', Cell: TextAlignLeftRenderer },
        { accessor: 'pkgs', Cell: FloatRenderer },
        { accessor: 'pUnit', Cell: (props: any) => <FloatRenderer decimals={0} {...props} />},
        { accessor: 'weight', Cell: FloatRenderer },
        { accessor: 'volume', Cell: FloatRenderer },
      ],
      footerRow: [
        {
          colspan: 2,
          renderer: () => NoOfContainerRenderer(hbl.hts.length)
        },
        {
          colspan: 1,
          renderer: () => <div></div>
        },
        {
          colspan: 2,
          renderer: () => TotaPkgsRenderer(hbl.hts.map(x => x.pkgs).reduce((acc:number,x) => acc + x, 0))
        },
        {
          colspan: 1,
          renderer: () => <div></div>
        },
        {
          colspan: 1,
          renderer: () => TotalKgsRenderer(hbl.hts.map(x => x.weight).reduce((acc:number,x) => acc + x, 0))
        },
        {
          colspan: 1,
          renderer: () => TotalCbmRenderer(hbl.hts.map(x => x.volume).reduce((acc:number,x) => acc + x, 0))
        },
      ],
    };
  
    return (
      <div>
        <SmartTable {...tableSettings} />
      </div>
    );
  }

  function HazardousInfo() {
    const tableSettings:ApplicableTableSettings = {
      customTableId: 'oi-mbl-h-info',
      tableName: 'oi-mbl-h-info',
      ...hazardousInfoSettings,
      height: '234px',
      data: hbl.hm.map(x => ({...x})),
      rowRenderers: [
        { accessor: 'no', Cell: IndicatorRenderer },
        { accessor: 'containerNo', Cell: TextAlignLeftRenderer },
        { accessor: 'hmCode', Cell: TextAlignLeftRenderer },
        { accessor: 'hmClass', Cell: TextAlignLeftRenderer },
        { accessor: 'hmQualifier', Cell: TextAlignLeftRenderer },
        { accessor: 'hmDescript', Cell: TextAlignLeftRenderer },
        { accessor: 'hmContact', Cell: TextAlignLeftRenderer },
        { accessor: 'hmUnpage', Cell: TextAlignLeftRenderer },
        { accessor: 'flashPoint', Cell: FloatRenderer },
        { accessor: 'flashUnit', Cell: TextAlignLeftRenderer },
        { accessor: 'negative', Cell: TextAlignLeftRenderer },
      ],
      footerRow: [
        {
          colspan: 3,
          renderer: () => NoOfContainerRenderer(hbl.hm.length)
        },
      ],
    };
  
    return (
      <div>
        <SmartTable {...tableSettings} />
      </div>
    );
  }

  function renderContent() {
    if (tab === 0) {
      return <BlInfo />
    }

    if (tab === 1) {
      return <DescriptionOfContainer />
    }

    if (tab === 2) {
      return <HazardousInfo />
    }

    return null;
  }

  return (
    <div className='report-popup'>
      <div style={{display: 'flex', borderBottom: '1px solid black', marginBottom: '10px', position: 'relative', width: '760px', left: '-40px', paddingLeft: '40px', paddingRight: '40px'}}>
        <div style={{flex: '1'}}>{renderTitleButton(<span>House B/L Information</span>, tab === 0, () => setTab(0), useDarkMode)}</div>
        <div style={{flex: '1'}}>{renderTitleButton(<span>Description of Container</span>, tab === 1, () => setTab(1), useDarkMode)}</div>
        <div style={{flex: '1'}}>{renderTitleButton(<span>Hazardous Information</span>, tab === 2, () => setTab(2), useDarkMode)}</div>
      </div>

      {renderContent()}

      <div className='clearfix'/>
      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          onClick={onPopupClose}>
          CANCEL
        </SecondaryButton>
      </div>
    </div>
  );
}

const ViewHbl = ({onPopupClose, selectedElementId}: { onPopupClose?: () => void, selectedElementId: string }) => {

  return (
    <ViewHblImpl id={selectedElementId} onPopupClose={onPopupClose} key={`viewHbl-details`}/>
  );
};
export default ViewHbl;
